import React, { useEffect, useRef, useState } from 'react';
import './ImageImportModal.css';

export type ImportCell = { x: number; y: number; color: string };

interface Props {
  open: boolean;
  onClose: () => void;
  onDone: (cells: ImportCell[]) => void;
  onProcessingChange?: (processing: boolean) => void;
}

// small helper: convert [r,g,b] to hex
function rgbToHex(r:number,g:number,b:number){
  return `#${((1<<24) + (r<<16) + (g<<8) + b).toString(16).slice(1).toUpperCase()}`;
}

// simple k-means clustering for colors
function kmeansColors(pixels: number[][], k: number, maxIter = 12){
  // pixels: array of [r,g,b]
  if(pixels.length === 0) return [];
  k = Math.max(1, Math.min(k, pixels.length));
  const centroids: number[][] = [];
  // init: pick random distinct samples
  const used = new Set<number>();
  while(centroids.length < k){
    const i = Math.floor(Math.random() * pixels.length);
    if(used.has(i)) continue;
    used.add(i);
    centroids.push(pixels[i].slice());
  }
  const assign = new Array(pixels.length).fill(0);
  for(let iter=0;iter<maxIter;iter++){
    let changed = false;
    // assign
    for(let i=0;i<pixels.length;i++){
      const p = pixels[i];
      let best = 0; let bd = Infinity;
      for(let c=0;c<centroids.length;c++){
        const cc = centroids[c];
        const d = (p[0]-cc[0])**2 + (p[1]-cc[1])**2 + (p[2]-cc[2])**2;
        if(d < bd){ bd = d; best = c; }
      }
      if(assign[i] !== best){ assign[i] = best; changed = true; }
    }
    // recompute
    const sums = new Array(centroids.length).fill(0).map(()=>[0,0,0,0]);
    for(let i=0;i<pixels.length;i++){ const a = assign[i]; sums[a][0]+=pixels[i][0]; sums[a][1]+=pixels[i][1]; sums[a][2]+=pixels[i][2]; sums[a][3]+=1; }
    for(let c=0;c<centroids.length;c++){
      if(sums[c][3] === 0) continue; // leave centroid
      const nr = Math.round(sums[c][0]/sums[c][3]);
      const ng = Math.round(sums[c][1]/sums[c][3]);
      const nb = Math.round(sums[c][2]/sums[c][3]);
      centroids[c][0]=nr; centroids[c][1]=ng; centroids[c][2]=nb;
    }
    if(!changed) break;
  }
  return centroids.map(c=>[Math.max(0,Math.min(255,Math.round(c[0]))),Math.max(0,Math.min(255,Math.round(c[1]))),Math.max(0,Math.min(255,Math.round(c[2])))]);
}

// separable gaussian blur applied to ImageData; accepts fractional sigma (std dev)
function gaussianKernel(sigma: number){
  if(sigma <= 0) return [1];
  const s = Math.max(0.1, sigma);
  const radius = Math.ceil(s * 3); // window covers ~3 sigma
  const len = radius * 2 + 1;
  const kernel = new Array(len);
  let sum = 0;
  for(let i=-radius;i<=radius;i++){ const v = Math.exp(-(i*i)/(2*s*s)); kernel[i+radius] = v; sum += v; }
  for(let i=0;i<kernel.length;i++) kernel[i] = kernel[i] / sum;
  return kernel;
}

function applyGaussianBlur(src: ImageData, w: number, h: number, sigma: number){
  if(sigma <= 0) return src;
  const kernel = gaussianKernel(sigma);
  const kr = Math.floor(kernel.length/2);
  const srcData = src.data;
  const tmp = new Uint8ClampedArray(srcData.length);
  // horizontal pass
  for(let y=0;y<h;y++){
    for(let x=0;x<w;x++){
      let r=0,g=0,b=0,a=0;
      for(let k=-kr;k<=kr;k++){
        const sx = Math.min(w-1, Math.max(0, x + k));
        const idx = (y * w + sx) * 4;
        const kval = kernel[k+kr];
        r += srcData[idx] * kval;
        g += srcData[idx+1] * kval;
        b += srcData[idx+2] * kval;
        a += srcData[idx+3] * kval;
      }
      const di = (y * w + x) * 4;
      tmp[di] = Math.round(r); tmp[di+1] = Math.round(g); tmp[di+2] = Math.round(b); tmp[di+3] = Math.round(a);
    }
  }
  // vertical pass (write back to src.data)
  const out = new Uint8ClampedArray(srcData.length);
  for(let x=0;x<w;x++){
    for(let y=0;y<h;y++){
      let r=0,g=0,b=0,a=0;
      for(let k=-kr;k<=kr;k++){
        const sy = Math.min(h-1, Math.max(0, y + k));
        const idx = (sy * w + x) * 4;
        const kval = kernel[k+kr];
        r += tmp[idx] * kval;
        g += tmp[idx+1] * kval;
        b += tmp[idx+2] * kval;
        a += tmp[idx+3] * kval;
      }
      const di = (y * w + x) * 4;
      out[di] = Math.round(r); out[di+1] = Math.round(g); out[di+2] = Math.round(b); out[di+3] = Math.round(a);
    }
  }
  const dst = new ImageData(w, h);
  dst.data.set(out);
  return dst;
}

export default function ImageImportModal(props: Props){
  const { open, onClose, onDone } = props;
  const fileRef = useRef<HTMLInputElement | null>(null);
  const previewRef = useRef<HTMLCanvasElement | null>(null);
  const urlRef = useRef<string | null>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [numColors, setNumColors] = useState(3);
  const [brightness, setBrightness] = useState(0); // -1..+1
  const [grayscale, setGrayscale] = useState(false);
  const [smoothness, setSmoothness] = useState(0); // 0..3 radius for gaussian blur
  const [bgColor, setBgColor] = useState('#ffffff');
  const [threshold, setThreshold] = useState(30); // 0..100 (percent of max RGB distance)
  const [bgEnabled, setBgEnabled] = useState(false);
  const [scale, setScale] = useState(0.5); // 0.05..1
  const [processing, setProcessing] = useState(false);
  const [processedCells, setProcessedCells] = useState<ImportCell[] | null>(null);
  const debounceRef = useRef<number | null>(null);
  const [processedSize, setProcessedSize] = useState<{w:number,h:number} | null>(null);
  const [noiseReduction, setNoiseReduction] = useState(0); // component size threshold (<=) to remove

  useEffect(()=>{ if(!open){ setImg(null); setProcessedCells(null); } },[open]);

  // helper to reset component state to defaults
  function resetState(){
    setImg(null);
    setNumColors(3);
    setBrightness(0);
    setGrayscale(false);
    setSmoothness(0);
    setBgColor('#ffffff');
    setThreshold(30);
    setBgEnabled(false);
    setScale(0.5);
    setProcessing(false);
    setProcessedCells(null);
    setProcessedSize(null);
    if(debounceRef.current){ window.clearTimeout(debounceRef.current); debounceRef.current = null; }
    if(urlRef.current){ try{ URL.revokeObjectURL(urlRef.current); }catch(e){} urlRef.current = null; }
  }

  // revoke object URL when modal closes
  useEffect(()=>{
    if(!open){ if(urlRef.current){ try{ URL.revokeObjectURL(urlRef.current); }catch(e){} urlRef.current = null; } }
  },[open]);

  function handleFile(file: File | null){
    if(!file) return;
    // revoke any previous object URL to avoid leaks
    if(urlRef.current){ try{ URL.revokeObjectURL(urlRef.current); }catch(e){} urlRef.current = null; }
    const url = URL.createObjectURL(file);
    urlRef.current = url;
    const image = new Image();
    image.onload = ()=>{ setImg(image); processImage(image); };
    image.onerror = ()=>{ try{ URL.revokeObjectURL(url); }catch(e){} urlRef.current = null; alert('Failed to load image'); };
    image.src = url;
  }

  // auto-reprocess when settings change (debounced)
  useEffect(()=>{
    if(!img) return;
    if(debounceRef.current) window.clearTimeout(debounceRef.current);
    // small debounce to avoid reprocessing while sliding
    debounceRef.current = window.setTimeout(()=>{ processImage(img); debounceRef.current = null; }, 350) as unknown as number;
    return ()=>{ if(debounceRef.current) window.clearTimeout(debounceRef.current); };
  },[numColors, brightness, grayscale, scale, img, bgColor, threshold, bgEnabled, smoothness, noiseReduction]);


  function hexToRgb(hex: string){
    const s = hex.replace('#','');
    const v = s.length === 3 ? s.split('').map(c=>c+c).join('') : s;
    const r = parseInt(v.slice(0,2),16); const g = parseInt(v.slice(2,4),16); const b = parseInt(v.slice(4,6),16);
    return [r,g,b];
  }

  function onDrop(e: React.DragEvent){ e.preventDefault(); if(e.dataTransfer.files && e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); }
  function onDragOver(e: React.DragEvent){ e.preventDefault(); }

  function processImage(image: HTMLImageElement){
    setProcessing(true);
    if(props.onProcessingChange) props.onProcessingChange(true);
    setTimeout(()=>{
      try{
        const w = Math.max(1, Math.round(image.width * scale));
        const h = Math.max(1, Math.round(image.height * scale));
        const tmp = document.createElement('canvas'); tmp.width = w; tmp.height = h;
        const ctx = tmp.getContext('2d'); if(!ctx) throw new Error('No ctx');
        // draw scaled
        ctx.drawImage(image, 0, 0, w, h);
          let id = ctx.getImageData(0,0,w,h);
          // apply gaussian blur if requested (smoothness > 0)
            if(smoothness && smoothness > 0){
            id = applyGaussianBlur(id, w, h, Math.max(0, smoothness));
            // put blurred data back into canvas so preview drawing uses it
            ctx.putImageData(id, 0, 0);
          }
        const pixels: number[][] = [];
        const maxSample = 4000;
        const bgRgb = bgEnabled ? hexToRgb(bgColor) : null;
        // map percent threshold (0..100) to squared RGB Euclidean distance
        const maxDist = Math.sqrt(255*255*3);
        const thresh = bgEnabled ? (threshold / 100) * maxDist : 0;
        const threshSq = thresh * thresh;
        // build pixel list and apply brightness/grayscale
        for(let y=0;y<h;y++){
          for(let x=0;x<w;x++){
            const i = (y*w + x) * 4;
            let r = id.data[i], g = id.data[i+1], b = id.data[i+2], a = id.data[i+3];
            if(a === 0) continue; // skip transparent
            // if background-ignore enabled and pixel is close to background color, treat as transparent (skip)
            if(bgRgb){ const dr = r - bgRgb[0], dg = g - bgRgb[1], db = b - bgRgb[2]; if((dr*dr + dg*dg + db*db) <= threshSq) continue; }
            if(grayscale){ const v = Math.round((r+g+b)/3); r=g=b=v; }
            if(Math.abs(brightness) > 1e-3){ const factor = 1 + brightness; r = Math.max(0,Math.min(255,Math.round(r*factor))); g = Math.max(0,Math.min(255,Math.round(g*factor))); b = Math.max(0,Math.min(255,Math.round(b*factor))); }
            pixels.push([r,g,b]);
          }
        }
        // sample if too many
        let sample = pixels;
        if(pixels.length > maxSample){
          sample = [];
          const step = Math.floor(pixels.length / maxSample);
          for(let i=0;i<pixels.length;i+=step) sample.push(pixels[i]);
        }
        const k = Math.max(1, Math.min(64, Math.round(numColors)));
        const centroids = kmeansColors(sample, k, 10);
        // Map each pixel to the nearest centroid by index, then optionally remove small components
        const idxMap = new Array(w*h).fill(-1);
        for(let yy=0; yy<h; yy++){
          for(let xx=0; xx<w; xx++){
            const i2 = (yy*w + xx)*4;
            let rr = id.data[i2], gg = id.data[i2+1], bb = id.data[i2+2], aa = id.data[i2+3];
            if(aa === 0){ idxMap[yy*w + xx] = -1; continue; }
            if(bgRgb){ const dr2 = rr - bgRgb[0], dg2 = gg - bgRgb[1], db2 = bb - bgRgb[2]; if((dr2*dr2 + dg2*dg2 + db2*db2) <= threshSq){ idxMap[yy*w + xx] = -1; continue; } }
            if(grayscale){ const v = Math.round((rr+gg+bb)/3); rr=gg=bb=v; }
            if(Math.abs(brightness) > 1e-3){ const factor = 1 + brightness; rr = Math.max(0,Math.min(255,Math.round(rr*factor))); gg = Math.max(0,Math.min(255,Math.round(gg*factor))); bb = Math.max(0,Math.min(255,Math.round(bb*factor))); }
            // choose nearest centroid
            let bestIdx = 0; let bestD = Infinity;
            for(let cIdx=0;cIdx<centroids.length;cIdx++){
              const cc = centroids[cIdx];
              const d = (rr-cc[0])*(rr-cc[0]) + (gg-cc[1])*(gg-cc[1]) + (bb-cc[2])*(bb-cc[2]);
              if(d < bestD){ bestD = d; bestIdx = cIdx; }
            }
            idxMap[yy*w + xx] = bestIdx;
          }
        }

        // run connected-component noise removal (replace tiny components with majority neighbor color)
        const nr = Math.max(0, Math.floor(noiseReduction));
        const cleaned = removeNoiseComponents(idxMap, w, h, nr, centroids.length);

        // write quantized preview into preview canvas (scaled up for visibility) and produce cells
        const cells: ImportCell[] = [];
        const previewCanvas = previewRef.current;
        if(previewCanvas){
          const pctx = previewCanvas.getContext('2d');
          if(pctx){
            const scaleUp = Math.max(1, Math.floor(400 / Math.max(w,h)));
            previewCanvas.width = w * scaleUp;
            previewCanvas.height = h * scaleUp;
            // remember processed cell dimensions for UI
            setProcessedSize({ w, h });
            pctx.imageSmoothingEnabled = false;
            for(let yy=0; yy<h; yy++){
              for(let xx=0; xx<w; xx++){
                const idx = cleaned[yy*w + xx];
                if(idx < 0){ pctx.clearRect(xx*scaleUp, yy*scaleUp, scaleUp, scaleUp); continue; }
                const pr = centroids[idx][0];
                const pg = centroids[idx][1];
                const pb = centroids[idx][2];
                const hex = rgbToHex(pr,pg,pb);
                cells.push({ x: xx, y: yy, color: hex });
                pctx.fillStyle = hex;
                pctx.fillRect(xx*scaleUp, yy*scaleUp, scaleUp, scaleUp);
              }
            }
          }
        }
        setProcessedCells(cells);
        setProcessing(false);
        if(props.onProcessingChange) props.onProcessingChange(false);
      }catch(err){
        console.error(err); setProcessing(false); if(props.onProcessingChange) props.onProcessingChange(false); alert('Error processing image');
      }
    }, 20);
  }

  function handleDone(){
    if(!processedCells){ alert('No processed image'); return; }
    onDone(processedCells);
    // reset state so subsequent opens start fresh
    resetState();
    onClose();
  }

  if(!open) return null;
  return (
    <div className="imodal-backdrop" onClick={()=>{ resetState(); onClose(); }}>
      <div className="imodal-panel" onClick={e=>e.stopPropagation()}>
        <h3>Import Image</h3>
        <div className="imodal-body">
          <div className="dropzone" onDrop={onDrop} onDragOver={onDragOver} onClick={()=>fileRef.current?.click()}>
            <input ref={fileRef} type="file" accept="image/*" style={{display:'none'}} onChange={e=>{ if(e.target.files && e.target.files[0]) handleFile(e.target.files[0]); e.currentTarget.value=''; }} />
            {!img && (
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:8}}>
                <div style={{fontSize:14,fontWeight:500}}>{'Click or drag & drop an image here'}</div>
                <div style={{marginTop:8,fontSize:12,color:'gray'}}>Accepted: PNG, JPG, GIF</div>
              </div>
            )}
            {img && (
              <div style={{width:'100%',height:'100%',display:'flex',alignItems:'stretch',justifyContent:'center'}}>
                <img alt="Original" src={img.src} style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:6,boxShadow:'0 2px 10px rgba(0,0,0,0.15)'}} />
              </div>
            )}
          </div>

          <div className="imodal-controls">
            <div className="control control-stepper">
              <label className="control-label">Colors</label>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <button className="stepper" onClick={()=>setNumColors(n=>Math.max(1, n-1))} aria-label="decrease colors">◀</button>
                <div className="stepper-value">{numColors}</div>
                <button className="stepper" onClick={()=>setNumColors(n=>Math.min(64, n+1))} aria-label="increase colors">▶</button>
              </div>
            </div>
            <div className="control control-stepper">
              <label className="control-label">Brightness</label>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <button className="stepper" onClick={()=>setBrightness(b=>parseFloat(Math.max(-0.8, +(b - 0.05)).toFixed(2)))} aria-label="decrease brightness">◀</button>
                <div className="stepper-value">{brightness.toFixed(2)}</div>
                <button className="stepper" onClick={()=>setBrightness(b=>parseFloat(Math.min(0.8, +(b + 0.05)).toFixed(2)))} aria-label="increase brightness">▶</button>
              </div>
              <input className="control-input range" type="range" min={-0.8} max={0.8} step={0.05} value={brightness} onChange={e=>setBrightness(parseFloat(e.target.value||'0'))} />
            </div>
            <div className="control horizontal">
              <label className="control-label"><input type="checkbox" checked={grayscale} onChange={e=>setGrayscale(e.target.checked)} /> <span style={{marginLeft:8}}>Grayscale</span></label>
            </div>
            <div className="control control-stepper">
              <label className="control-label">Scale</label>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <button className="stepper" onClick={()=>setScale(s=>parseFloat(Math.max(0.01, +(s - 0.01)).toFixed(2)))} aria-label="decrease scale">◀</button>
                <div className="stepper-value">{scale.toFixed(2)}</div>
                <button className="stepper" onClick={()=>setScale(s=>parseFloat(Math.min(1, +(s + 0.01)).toFixed(2)))} aria-label="increase scale">▶</button>
              </div>
              <input className="control-input range" type="range" min={0.01} max={1} step={0.01} value={scale} onChange={e=>setScale(parseFloat(e.target.value||'0.5'))} />
            </div>
            <div className="control control-stepper">
              <label className="control-label">Smoothness</label>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <button className="stepper" onClick={()=>setSmoothness(s=>parseFloat(Math.max(0, +(s - 0.1)).toFixed(1)))} aria-label="decrease smoothness">◀</button>
                <div className="stepper-value">{smoothness.toFixed(1)}</div>
                <button className="stepper" onClick={()=>setSmoothness(s=>parseFloat(Math.min(3, +(s + 0.1)).toFixed(1)))} aria-label="increase smoothness">▶</button>
              </div>
              <input className="control-input range" type="range" min={0} max={3} step={0.1} value={smoothness} onChange={e=>setSmoothness(parseFloat(e.target.value||'0'))} />
            </div>
            <div className="control control-stepper">
              <label className="control-label">Noise reduction</label>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <button className="stepper" onClick={()=>setNoiseReduction(n=>Math.max(0, n-1))} aria-label="decrease noise reduction">◀</button>
                <div className="stepper-value">{noiseReduction}</div>
                <button className="stepper" onClick={()=>setNoiseReduction(n=>Math.min(10, n+1))} aria-label="increase noise reduction">▶</button>
              </div>
              <input className="control-input range" type="range" min={0} max={10} step={1} value={noiseReduction} onChange={e=>setNoiseReduction(parseInt(e.target.value||'0'))} />
            </div>
            <div className="control">
              <label className="control-label"><input type="checkbox" checked={bgEnabled} onChange={e=>setBgEnabled(e.target.checked)} /> <span style={{marginLeft:8}}>Select background color to ignore</span></label>
            </div>
            {bgEnabled && (
              <>
                <div className="control">
                  <label className="control-label">Background color</label>
                  <input className="control-input color fullwidth" type="color" value={bgColor} onChange={e=>setBgColor(e.target.value)} />
                </div>
                <div className="control control-stepper">
                  <label className="control-label">Threshold <span style={{marginLeft:8,fontWeight:600}}>{threshold}%</span></label>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <button className="stepper" onClick={()=>setThreshold(t=>Math.max(0, t-1))} aria-label="decrease threshold">◀</button>
                    <div className="stepper-value">{threshold}%</div>
                    <button className="stepper" onClick={()=>setThreshold(t=>Math.min(100, t+1))} aria-label="increase threshold">▶</button>
                  </div>
                  <input className="control-input range" type="range" min={0} max={100} value={threshold} onChange={e=>setThreshold(parseInt(e.target.value||'0'))} />
                </div>
              </>
            )}
          </div>

          <div className="imodal-preview">
            <canvas ref={previewRef} style={{border:'1px solid #ccc', background:'#fff', width:'100%', height:'100%'}} />
            {processing && <div style={{position:'absolute',right:18,top:12}} />}
            <div className="imodal-dim">{processedSize ? `${processedSize.w} × ${processedSize.h} cells` : ''}</div>
            {processedSize && (processedSize.w * processedSize.h > 10000) && (
              <div className="imodal-warning" style={{fontSize: 'small'}}>
                Just so you know, this is {processedSize.w * processedSize.h} cells...
              </div>
            )}
            {processedSize && (processedSize.w * processedSize.h > 60000) && (
              <div className="imodal-warning">
                <strong>Warning:</strong> Large imports may cause performance issues.
              </div>
            )}
          </div>
          {processing && <div className="imodal-spinner" aria-hidden="true" />}
        </div>
        <div className="imodal-footer">
          <button className="imodal-done" onClick={handleDone} disabled={!processedCells || processing}>{processing ? 'Processing...' : 'Insert!'}</button>
        </div>
      </div>
    </div>
  );
}

// remove small connected components from an index map of centroid indices
function removeNoiseComponents(indexMap: number[], w: number, h: number, threshold: number, k: number){
  if(!threshold || threshold <= 0) return indexMap;
  const out = indexMap.slice();
  const visited = new Uint8Array(w*h);
  const stack: number[] = [];
  for(let y=0;y<h;y++){
    for(let x=0;x<w;x++){
      const pos = y*w + x;
      if(visited[pos]) continue;
      const val = out[pos];
      if(val < 0){ visited[pos] = 1; continue; }
      // collect component
      stack.length = 0;
      stack.push(pos);
      visited[pos] = 1;
      const comp: number[] = [];
      while(stack.length){
        const p = stack.pop() as number;
        comp.push(p);
        const px = p % w; const py = Math.floor(p / w);
        // neighbors 4-connected
        if(px > 0){ const np = p-1; if(!visited[np] && out[np] === val){ visited[np]=1; stack.push(np); } }
        if(px < w-1){ const np = p+1; if(!visited[np] && out[np] === val){ visited[np]=1; stack.push(np); } }
        if(py > 0){ const np = p-w; if(!visited[np] && out[np] === val){ visited[np]=1; stack.push(np); } }
        if(py < h-1){ const np = p+w; if(!visited[np] && out[np] === val){ visited[np]=1; stack.push(np); } }
      }
      if(comp.length <= threshold){
        // tally neighbor colors around the component
        const counts = new Array(k).fill(0);
        for(const p of comp){
          const px = p % w; const py = Math.floor(p / w);
          if(px > 0){ const np = p-1; if(out[np] >= 0 && out[np] !== val) counts[out[np]]++; }
          if(px < w-1){ const np = p+1; if(out[np] >= 0 && out[np] !== val) counts[out[np]]++; }
          if(py > 0){ const np = p-w; if(out[np] >= 0 && out[np] !== val) counts[out[np]]++; }
          if(py < h-1){ const np = p+w; if(out[np] >= 0 && out[np] !== val) counts[out[np]]++; }
        }
        // choose majority neighbor, otherwise skip
        let bestIdx = -1; let bestCount = -1;
        for(let ci=0;ci<k;ci++){ if(counts[ci] > bestCount){ bestCount = counts[ci]; bestIdx = ci; } }
        if(bestIdx >= 0 && bestCount > 0){
          for(const p of comp) out[p] = bestIdx;
        }
      }
    }
  }
  return out;
}
